import React from "react";
import {
  Typography,
  makeStyles,
  Button,
  Grid,
  Box,
  Container,
  Card,
  CardContent,
  Divider,
  Link,
  Paper,
} from "@material-ui/core";
import Slider from "react-slick";
import StarRateIcon from "@material-ui/icons/StarRate";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2, 0),
    background: theme.palette.type === "dark" ? "#000" : "#f5f5f5", // Updated to #000 for dark theme
    color: theme.palette.type === "dark" ? "#fff" : "#333",
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(4),
    },
  },
  title: {
    fontSize: "1.8rem",
    fontWeight: "bold",
    marginBottom: theme.spacing(1),
    textAlign: "center",
    color: theme.palette.type === "dark" ? "#fff" : "#000",
    [theme.breakpoints.up("md")]: {
      fontSize: "2.5rem",
    },
  },
  subtitle: {
    fontSize: "1.2rem",
    fontWeight: "medium",
    textAlign: "center",
    marginBottom: theme.spacing(3),
    color: theme.palette.type === "dark" ? "#aaa" : "#555",
  },
  body: {
    fontSize: "1.1rem",
    marginBottom: theme.spacing(2),
    lineHeight: "1.5",
    color: theme.palette.type === "dark" ? "#aaa" : "#666",
  },
  button: {
    marginTop: theme.spacing(2),
    color: "#ffffff",
    backgroundColor: "#8601af",
    "&:hover": {
      backgroundColor: "#8601af",
    },
  },
  carouselImage: {
    height: "200px",
    width: "100%",
    objectFit: "cover",
    [theme.breakpoints.up("md")]: {
      height: "300px",
    },
  },
  storyTitle: {
    fontSize: "1.8rem",
    fontWeight: "bold",
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(3),
    textAlign: "center",
    color: theme.palette.type === "dark" ? "#fff" : "#777",
  },
  storyBody: {
    fontSize: "1.1rem",
    marginBottom: theme.spacing(2),
    lineHeight: "1.5",
    color: theme.palette.type === "dark" ? "#aaa" : "#666",
  },
  divider: {
    margin: theme.spacing(3, 0),
    backgroundColor: theme.palette.type === "dark" ? "#555" : "#e0e0e0",
  },
  mission: {
    fontStyle: "italic",
    marginTop: theme.spacing(2),
    textAlign: "center",
    color: theme.palette.type === "dark" ? "#aaa" : "#777",
  },
  link: {
    textDecoration: "none",
    color: theme.palette.primary.main,
  },
  storyCard: {
    backgroundColor: theme.palette.type === "dark" ? "#121212" : "#fafafa",
    padding: theme.spacing(2),
    borderRadius: "10px",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
  },
  carouselContainer: {
    position: "relative",
    overflow: "hidden",
    borderRadius: "10px",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
  },
  carouselCaption: {
    position: "absolute",
    bottom: "10%",
    left: "10%",
    color: "#fff",
    background: "rgba(0, 0, 0, 0.5)",
    padding: theme.spacing(1, 2),
    borderRadius: "5px",
  },
  ctaContainer: {
    textAlign: "center",
    marginTop: theme.spacing(4),
  },
  ctaButton: {
    padding: theme.spacing(1, 2),
    fontSize: "1rem",
    fontWeight: "bold",
    backgroundColor: "#8601af",
    color: "#ffffff",
    "&:hover": {
      backgroundColor: "#8601af",
    },
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(1, 4),
      fontSize: "1.1rem",
    },
  },
  testimonialCard: {
    backgroundColor: theme.palette.type === "dark" ? "#121212" : "#fafafa",
    padding: theme.spacing(2),
    borderRadius: "10px",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
    textAlign: "center",
  },
  testimonialText: {
    fontStyle: "italic",
    color: theme.palette.type === "dark" ? "#aaa" : "#666",
  },
  testimonialAuthor: {
    fontWeight: "bold",
    color: theme.palette.type === "dark" ? "#fff" : "#333",
  },
  starIcon: {
    color: "#FFD700",
  },
}));

const WelcomeMessage = () => {
  const classes = useStyles();

  const slides = [
    {
      image: "/assets/images/group_tutorial.webp",
      caption: "Empowering Students to Achieve Success",
    },
    {
      image: "/assets/images/home_tutorial.webp",
      caption: "Quality Education for All Ages",
    },
    {
      image: "/assets/images/one_on_one_tutorial.webp",
      caption: "Join Us in Making Education Accessible",
    },
    // Add more slides here...
  ];

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    pauseOnHover: true,
  };

  // Custom testimonials
  const testimonials = [
    {
      text: "The tutors here are amazing! They helped me improve my grades significantly.",
      author: "John Doe",
      rating: 5,
    },
    {
      text: "I love the personalized approach to learning. It made a huge difference for me.",
      author: "Jane Smith",
      rating: 4,
    },
    {
      text: "The online tutoring sessions are convenient and effective. Highly recommended!",
      author: "Alice Johnson",
      rating: 5,
    },
    // Add more testimonials here...
  ];

  return (
    <Paper className={classes.container} elevation={3}>
      <Typography variant="h4" className={classes.title}>
        Welcome to NgeMbalenhle Tutoring Services
      </Typography>
      <Typography variant="h6" className={classes.subtitle}>
        Empowering Students to Achieve Their Dreams Through Education
      </Typography>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Box className={classes.carouselContainer}>
            <Slider {...settings}>
              {slides.map((slide, index) => (
                <Box key={index} position="relative">
                  <img
                    src={slide.image}
                    alt={`Slide ${index}`}
                    className={classes.carouselImage}
                  />
                  <Typography variant="h6" className={classes.carouselCaption}>
                    {slide.caption}
                  </Typography>
                </Box>
              ))}
            </Slider>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="body1" className={classes.body}>
            Our primary goal in all business decisions is to achieve a high
            level of customer satisfaction with all that we provide. We provide
            tutorial lessons for primary school students, high school students,
            and tertiary students. This simple yet effective approach has fueled
            our growth since opening in 2020, and will continue to do so for
            years down the road.{" "}
            <Link href="/about" className={classes.link}>
              Learn more about our journey.
            </Link>
          </Typography>
          <Typography variant="body1" className={classes.mission}>
            "We believe in the power of education to transform lives."
          </Typography>
          <Button variant="contained" className={classes.button}>
            Discover what we’re all about
          </Button>
        </Grid>
        <Grid item xs={12}>
          <Divider className={classes.divider} />
          <Typography variant="h4" className={classes.storyTitle}>
            Our Story
          </Typography>
          <Card className={classes.storyCard}>
            <CardContent>
              <Typography variant="body1" className={classes.storyBody}>
                NgeMbalenhle Tutoring Services was founded with a mission to
                provide quality education to students of all ages. Our story
                began in a small classroom, and we have since grown into a
                leading tutoring service provider. Our dedicated team of
                educators is committed to fostering a love for learning and
                helping students reach their full potential. Join us on our
                journey to make education accessible and enjoyable for all.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Divider className={classes.divider} />
          <Typography variant="h4" className={classes.storyTitle}>
            What Our Students Say
          </Typography>
          <Grid container spacing={3}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Paper className={classes.testimonialCard}>
                  <Typography
                    variant="body1"
                    className={classes.testimonialText}
                  >
                    "{testimonial.text}"
                  </Typography>
                  <Typography
                    variant="h6"
                    className={classes.testimonialAuthor}
                  >
                    - {testimonial.author}
                  </Typography>
                  <Box display="flex" justifyContent="center">
                    {[...Array(testimonial.rating)].map((star, i) => (
                      <StarRateIcon key={i} className={classes.starIcon} />
                    ))}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Grid>
        <Grid item xs={12} className={classes.ctaContainer}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            className={classes.ctaButton}
          >
            Start Your Learning Journey Today
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default WelcomeMessage;
